import { FC, ReactNode, useMemo } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './UserRoleTable.translations.json';
import { ConfigProvider, Flex } from 'antd';
import {
  CreateFormTableRowButton,
  DeleteFormTableRowButton,
  FormDateRangePicker,
  FormField,
  FormSelect,
  TanstackFormTable,
  getFormTableFieldName,
  ISODate,
  getDatePeriodString,
  getIsoDateToday,
  sortPeriod,
  UserRoleFormDataType,
  defaultUserRoleFormData,
  RoleSelect,
  Role,
  useUserRolesQuery,
  TextEllipsis,
} from '@atlas-frontend/sdk';
import { createColumnHelper } from '@tanstack/react-table';
import { useWatch } from 'react-hook-form';
import { UiLink } from '../../paths/UiLink';
import { path } from '../../paths';

const { addTranslations, useTranslation } = i18nNsFactory('user-role-table');
addTranslations(translations);

const columnHelper = createColumnHelper<UserRoleFormDataType>();

export const UserRolesFormTable = () => {
  const { t } = useTranslation();
  const { data: allRoles = [] } = useUserRolesQuery();

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: t('column.id'),
        meta: { width: '20ch', className: 'sticky-left' },
        enableColumnFilter: false,
        cell: (props) => {
          const name = getFormTableFieldName(props);
          return (
            <FormField
              name={name}
              size="small"
              preview={{
                previewOnly: Boolean(props.row.original.id),
                render: (value) => (
                  <TextEllipsis>
                    <UiLink
                      id={`user-role-name-${props.row.index}`}
                      path={path.roleDetails}
                      params={{ id: value }}
                      target="_blank"
                    >
                      {allRoles.find((role) => role.id === Number(value))
                        ?.name ?? value}
                    </UiLink>
                  </TextEllipsis>
                ),
              }}
            >
              <FormSelect size="small" name={name} autoFocus defaultOpen>
                <RoleSelect
                  id={['roles', props.row.index, 'id'].join('-')}
                  roles={allRoles}
                />
              </FormSelect>
            </FormField>
          );
        },
      }),
      columnHelper.accessor('permissions', {
        header: t('column.permissions'),
        meta: { width: '60%' },
        enableColumnFilter: false,
        cell: (props) => (
          <FormField
            name={getFormTableFieldName(props)}
            size="small"
            preview={{
              previewOnly: true,
              render: () => (
                <SelectedRoleRenderer
                  formDataIndex={props.row.index}
                  roles={allRoles}
                  render={(role) => (
                    <TextEllipsis>
                      {role.permissions.map(({ name }) => name).join(', ')}
                    </TextEllipsis>
                  )}
                />
              ),
            }}
          />
        ),
      }),
      columnHelper.accessor('accessGroups', {
        header: t('column.accessGroups'),
        meta: { width: '40%' },
        enableColumnFilter: false,
        cell: (props) => (
          <FormField
            name={getFormTableFieldName(props)}
            size="small"
            preview={{
              previewOnly: true,
              render: () => (
                <SelectedRoleRenderer
                  formDataIndex={props.row.index}
                  roles={allRoles}
                  render={(role) => (
                    <TextEllipsis>
                      {role.accessGroups.map(({ name }) => name).join(', ')}
                    </TextEllipsis>
                  )}
                />
              ),
            }}
          />
        ),
      }),
      columnHelper.accessor('validPeriod', {
        header: t('column.validPeriod'),
        meta: { width: 266 },
        enableColumnFilter: false,
        cell: (props) => {
          const name = getFormTableFieldName(props);
          return (
            <FormField
              name={name}
              size="small"
              preview={{
                render: (value) => getDatePeriodString(value?.[0], value?.[1]),
              }}
            >
              <FormDateRangePicker
                size="small"
                allowEmpty={[true, true]}
                autoFocus
                name={name}
              />
            </FormField>
          );
        },
        sortingFn: (a, b) =>
          sortPeriod(a.original.validPeriod, b.original.validPeriod),
      }),
      columnHelper.accessor('operations' as any, {
        header: () => (
          <Flex justify="center" gap="small">
            <CreateFormTableRowButton
              autoFocusField="id"
              defaultRowValues={getDefaultRowValues()}
            />
          </Flex>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        cell: (props) => (
          <Flex justify="center" gap="small">
            <DeleteFormTableRowButton context={props} />
          </Flex>
        ),
        meta: {
          width: 88,
          className: 'sticky-right',
        },
      }),
    ],
    [allRoles, t],
  );

  return (
    <ConfigProvider renderEmpty={() => t('empty-fallback')}>
      <TanstackFormTable
        name="roles"
        columns={columns}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
    </ConfigProvider>
  );
};

const getDefaultRowValues = () => ({
  ...defaultUserRoleFormData,
  validPeriod: [getIsoDateToday(), null] as [ISODate | null, null],
});

const SelectedRoleRenderer: FC<{
  formDataIndex: number;
  roles: Role[];
  render: (role: Role) => ReactNode;
}> = ({ formDataIndex, roles, render }) => {
  const id = useWatch({
    name: `roles.${formDataIndex}.id`,
  }) as string;
  const role = roles.find((role) => role.id === Number(id));
  return role ? render(role) : null;
};
